import { defineStore } from 'pinia';
import { HEARTBEAT, AVISOS } from '@/config/offline';
import { db } from '@/db/posDb';
import { useAuth } from './auth';

// Etapa 3D (B): último desfase conocido del reloj (sobrevive al cierre de sesión y al F5)
function leerRelojGuardado() {
  try { return JSON.parse(localStorage.getItem('zapi_reloj')) || null; } catch (e) { return null; }
}

// Estado global de la conexión con el servidor (modo offline).
// modoOffline lo cambia SOLO el heartbeat, a través de registrarPing().
export const useConexion = defineStore({
  id: 'conexion',
  state: () => ({
    // Etapa 3D (B): desfase del reloj; se lee de localStorage en cada creación o $reset del store
    desfaseRelojMs: (leerRelojGuardado() || { desfaseMs: null }).desfaseMs, // equipo − servidor; null = nunca medido
    relojMedidoEn: (leerRelojGuardado() || { medidoEn: null }).medidoEn,
    relojBase: null, // Date.now() − performance.now() al medir; solo existe en esta carga de la página
    // Etapa 3D (D6): resultado de navigator.storage.persist() (null = no se pidió o no existe)
    almacenamientoPersistente: null,
    modoOffline: false,
    fallosConsecutivos: 0,
    exitosConsecutivos: 0,
    ultimoPingOk: null,
    ultimoStatus: null,
    verificando: false,
    sesionExpirada: false,
    // Cola de ventas: los escribe la Etapa 3; en la Etapa 2 quedan en 0
    ventasEnCola: 0,
    ventasRechazadas: 0,
    // Etapa 3D: ventas propias en cola (las de otros usuarios no se pueden enviar con esta sesión)
    ventasEnColaUsuario: 0,
    // Etapa 3D (A): pendientes con ≥ 60 h y ≥ 72 h según la hora del servidor; nombres de los cajeros ajenos
    ventasAntiguas: 0,
    ventasAntiguasAjenas: 0,
    ventasVencidas: 0,
    horasVentaMasAntigua: 0,
    cajerosConVentasAntiguas: [],
    sincronizando: false,
    // Etapa 3C: 403 en /ventas/sync (usuario sin «Venta POS»): el sync se pausa hasta otro inicio de sesión
    syncSinPermiso: false,
    // Etapa 4 (d): cierres de caja guardados en este equipo sin enviar (cierres_cola 'pendiente') y sus cajas
    cierresPendientes: 0,
    cajasConCierrePendiente: [],
    // Etapa 6: hay una versión nueva del sistema esperando (la franja muestra «Actualizar»)
    versionNueva: false,
    // Etapa 6: el POS tiene productos en el carrito («Actualizar» pide confirmación antes de recargar)
    carritoConProductos: false,
    // Sube cada vez que el guard de rutas cancela una navegación (el banner muestra el aviso)
    navegacionesBloqueadas: 0
  }),
  actions: {
    // Resultado de un ping: 3 fallos seguidos -> offline; 1 éxito -> online
    registrarPing(ok, status) {
      this.ultimoStatus = status;
      if (ok) {
        this.fallosConsecutivos = 0;
        this.exitosConsecutivos += 1;
        this.ultimoPingOk = new Date();
        if (this.exitosConsecutivos >= HEARTBEAT.exitosParaOnline) this.modoOffline = false;
        // Ítem 14: el heartbeat usa /ping sin token (conAuth: false), así que esta línea ya no marca nada; queda por si
        // se vuelve a un heartbeat con token. Antes: «Mientras el heartbeat use caja/verificar (con token), un 401
        // indica sesión vencida»
        if (status === 401 && HEARTBEAT.conAuth) this.sesionExpirada = true;
      } else {
        this.exitosConsecutivos = 0;
        this.fallosConsecutivos += 1;
        if (this.fallosConsecutivos >= HEARTBEAT.fallosParaOffline) this.modoOffline = true;
      }
    },
    // Botón «Reintentar» del banner (import dinámico para no crear un ciclo store <-> heartbeat)
    async forzarPing() {
      const { pingAhora } = await import('@/services-offline/heartbeat');
      return pingAhora();
    },
    avisarBloqueo() {
      this.navegacionesBloqueadas += 1;
    },
    // Etapa 3B: contadores para el banner, ZapiEstadoSync y avisos (los llama la cola tras cada cambio)
    async refrescarContadoresCola() {
      // Etapa 3D (A): antes solo contaba; ahora lee las filas para la edad y el usuario. Antes:
      // this.ventasEnCola = await db.cola_ventas.where('estado').anyOf('pendiente', 'enviando').count();
      const enCola = await db.cola_ventas.where('estado').anyOf('pendiente', 'enviando').toArray();
      const auth = useAuth();
      const userId = auth.user ? Number(auth.user.id) : null;
      const ahora = this.ahoraServidorMs();
      const horas = (v) => (ahora - Date.parse(v.fecha_venta_cliente)) / 3600000;
      const antiguas = enCola.filter((v) => horas(v) >= AVISOS.horasVentaAntigua);
      this.ventasEnCola = enCola.length;
      this.ventasEnColaUsuario = enCola.filter((v) => Number(v.user_id) === userId).length;
      this.ventasAntiguas = antiguas.length;
      this.ventasVencidas = antiguas.filter((v) => horas(v) >= AVISOS.horasLimiteServidor).length;
      this.horasVentaMasAntigua = enCola.length ? Math.max(...enCola.map(horas)) : 0;
      const antiguasAjenas = antiguas.filter((v) => Number(v.user_id) !== userId);
      this.ventasAntiguasAjenas = antiguasAjenas.length;
      this.cajerosConVentasAntiguas = [...new Set(antiguasAjenas.map((v) => (v.ticket && v.ticket.cajero) || `usuario ${v.user_id}`))];
      this.ventasRechazadas = await db.cola_ventas.where('estado').equals('rechazada').count();
      // Etapa 4 (d)
      const cierres = await db.cierres_cola.where('estado').equals('pendiente').toArray();
      this.cierresPendientes = cierres.length;
      this.cajasConCierrePendiente = [...new Set(cierres.map((c) => c.caja_id))];
    },
    // Etapa 3D (B): medición del reloj con la cabecera Date de una respuesta (fetchWrapper.raw → reloj)
    registrarReloj({ servidorMs, equipoMs, rttMs }) {
      if (Number.isNaN(servidorMs) || rttMs > AVISOS.rttMaxMedicionMs) return;
      this.desfaseRelojMs = Math.round(equipoMs - servidorMs);
      this.relojMedidoEn = Date.now();
      this.relojBase = Date.now() - performance.now();
      localStorage.setItem('zapi_reloj', JSON.stringify({ desfaseMs: this.desfaseRelojMs, medidoEn: this.relojMedidoEn }));
    },
    // Desfase utilizable, o null si nunca se midió o si la hora del equipo saltó desde la medición
    desfaseVigente() {
      if (this.desfaseRelojMs === null) return null;
      if (this.relojBase !== null && Math.abs(Date.now() - performance.now() - this.relojBase) > AVISOS.saltoRelojMs) return null;
      return this.desfaseRelojMs;
    },
    // Hora estimada del servidor (sin desfase conocido, la del equipo)
    ahoraServidorMs() {
      return Date.now() - (this.desfaseVigente() || 0);
    },
    // D3 (a) simplificada: solo se corrige con el equipo ADELANTADO (desfase > 0) y ≥ 30 s, con el último
    // desfase conocido (también el de localStorage). Atrasado o nunca medido: la hora del equipo, sin corregir.
    fechaVentaCorregida() {
      const fechaEquipo = new Date();
      const desfase = this.desfaseRelojMs;
      const desfaseAplicadoMs = desfase !== null && desfase >= AVISOS.desfaseMinCorreccionMs ? desfase : 0;
      return { fecha: new Date(fechaEquipo.getTime() - desfaseAplicadoMs), fechaEquipo, desfaseAplicadoMs };
    }
  }
});
